<script lang="ts" setup>
import { BaseTabVariant, RoutesEnum, SlidersPerView } from '~/enums'
import { GAME_LOBBY_TABS, LOBBY_TAB_SWIPER_OPTIONS } from '~/resources'
import { useSwiper } from '~/composables/common/use-swiper'
import { useLobbyGameFilter } from '~/composables/game/use-lobby-game-filter'
import type { Swiper as SwiperType } from 'swiper/types'
import type { TabItem } from '~/types/app'

const {
  routeProvider,
  routeCategory,
  filterProviders,
  providers,
  changeProvider,
} = useLobbyGameFilter()
const { modules, SwiperSlide, Swiper } = useSwiper()
const providerCurrentSlideIndex = ref()
const swiperRef = ref()
const lobbyGameRefFilter = ref<HTMLDivElement | null>(null)

onMounted(() => {
  const initialSlide = providers.value.findIndex(
    (item) => item.key === routeProvider.value,
  )
  providerCurrentSlideIndex.value = initialSlide
})

const slideToIndex = (offset = 0) => {
  const index = filterProviders.value.findIndex(
    (tab: TabItem) => tab.key === routeProvider.value,
  )
  swiperRef.value?.slideTo(index < 1 ? 0 : index + offset, 300, false)
}

const initSwiperRef = (swiperInstance: SwiperType) => {
  swiperRef.value = swiperInstance
  nextTick(() => slideToIndex(-1))
}

watch(routeProvider, () => slideToIndex(-3))
</script>

<template>
  <div class="lobby container">
    <div class="lobby__header">
      <div class="lobby__header-left">
        <h2 class="lobby__header-left-title">Trò Chơi Đa Dạng</h2>
        <p class="lobby__header-left-subtitle">Thưởng khủng mỗi ngày</p>
      </div>
      <img
        class="lobby__header-img"
        src="/images/lobby/banner-lobby-header.webp"
        alt="Banner Lobby"
        width="1000"
        height="145"
      />
    </div>
    <div ref="lobbyGameRefFilter" class="lobby-swiper-item-wrapper">
      <div class="swiper-arrow">
        <div class="swiper-arrow__prev swiper-button-prev">
          <span class="icon-arrow icon-left" />
        </div>
        <div class="swiper-arrow__next swiper-button-next">
          <span class="icon-arrow icon-right" />
        </div>
      </div>
      <BaseSwiperTabs
        v-model="routeCategory"
        :tabs="GAME_LOBBY_TABS"
        :variant="BaseTabVariant.Large"
        :options="LOBBY_TAB_SWIPER_OPTIONS"
        :next-el="'.swiper-arrow__next'"
        :prev-el="'.swiper-arrow__prev'"
      >
        <template #item="{ item, selected }">
          <NuxtLink :to="`${RoutesEnum.LobbyGame}/${item.key}`">
            <div class="lobby-swiper-item" :class="{ active: selected }">
              <span :class="[item.icon, 'lobby-swiper-item__icon']" />
              <span class="lobby-swiper-item__title">{{ item.title }}</span>
            </div>
          </NuxtLink>
        </template>
      </BaseSwiperTabs>
    </div>
    <div class="lobby-provider">
      <div class="categories">
        <div class="categories__content">
          <div class="swiper-arrow-provider">
            <div class="swiper-arrow-provider__prev swiper-button-prev">
              <span class="icon-arrow icon-left" />
            </div>
            <div class="swiper-arrow-provider__next swiper-button-next">
              <span class="icon-arrow icon-right" />
            </div>
          </div>
          <Swiper
            v-if="filterProviders.length"
            :modules="modules"
            :slides-per-view="SlidersPerView.Auto"
            :space-between="8"
            :grab-cursor="true"
            :mousewheel="true"
            :observer="true"
            :observe-parents="true"
            :navigation="{
              nextEl: '.swiper-arrow-provider__next',
              prevEl: '.swiper-arrow-provider__prev',
            }"
            @swiper="initSwiperRef"
          >
            <SwiperSlide
              v-for="provider in filterProviders"
              :key="provider.key"
              :value="provider.key"
            >
              <div
                class="cursor-pointer lobby-provider__item"
                @click.prevent="changeProvider(provider.key ?? '')"
              >
                <a
                  :href="`${RoutesEnum.LobbyGame}/${routeCategory}/${provider.key}`"
                  class="pointer-events-none"
                >
                  <VBtn
                    :class="{
                      'v-btn--selected': provider?.key === routeProvider,
                    }"
                    rounded
                  >
                    {{ provider.title }}
                  </VBtn>
                </a>
              </div>
              <div v-if="provider.label" class="categories__item-label">
                <img
                  class="img-label"
                  :src="provider.label"
                  :alt="`${provider.title} label`"
                  width="32"
                  height="32"
                />
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '/assets/styles/components/desktop/pages/lobby-game/filter.scss';
@import '/assets/styles/components/responsive/desktop/lobby-game/index.scss';
</style>
